import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Upload as UploadIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Send as SendIcon,
  Add as AddIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync as SyncIcon,
  Code as CodeIcon,
  PlayArrow as PlayIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import hackathonService from '../../services/hackathonService';

// Design System Colors (matching TrackingDashboard)
const colors = {
  primary: '#485869',
  primaryLight: '#6c7c8c',
  primaryDark: '#3A4754',
  success: '#66CC33',
  error: '#fa5b5b',
  warning: '#FF9933',
  info: '#4DBCF3',
  background: '#F5F6F7',
  paper: '#FFFFFF',
  textPrimary: '#485869',
  textSecondary: '#6B757F',
  grey50: '#F5F6F7',
  grey200: '#C8CDD2',
  grey500: '#6B757F',
};

// Role colors for hackathon team members
const roleColors = {
  senior_dev: '#8B5CF6',
  junior_dev: '#3B82F6',
  fa_operator: '#10B981',
  test_operator: '#F59E0B',
  designer: '#EC4899',
  dev_operator: '#3B82F6',
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

// Stats Card Component
const StatCard = ({ title, value, subtitle, color = colors.primary }) => (
  <Card elevation={2} sx={{ height: '100%', bgcolor: colors.paper, borderRadius: 2 }}>
    <CardContent sx={{ p: 2 }}>
      <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500, fontSize: '0.75rem' }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Team Card Component
const TeamCard = ({ team, kpiConfigs, onScoreTeam, onSyncTracking }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        <GroupIcon sx={{ color: 'white', fontSize: 28 }} />

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
              {team.team_name}
            </Typography>
            {team.rank && (
              <Chip
                label={`#${team.rank} ${team.rank === 1 ? '🥇' : team.rank === 2 ? '🥈' : team.rank === 3 ? '🥉' : ''}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }}
              />
            )}
          </Box>
          {team.assigned_module && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Module: {team.assigned_module.name}
            </Typography>
          )}
        </Box>

        {/* Team Stats */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Members">
            <Chip
              icon={<PersonIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={team.members?.length || 0}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Tooltip>
          <Tooltip title="Score">
            <Chip
              icon={<TrophyIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={(team.total_score || 0).toFixed(1)}
              size="small"
              sx={{ bgcolor: colors.success, color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, bgcolor: colors.grey50 }}>
          {/* Members */}
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>
            Team Members
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {team.senior_dev && (
              <Chip
                size="small"
                label={`Senior: ${team.senior_dev.name || team.senior_dev.employee_name}`}
                sx={{ bgcolor: roleColors.senior_dev, color: 'white' }}
              />
            )}
            {team.fa_operator && (
              <Chip
                size="small"
                label={`FA: ${team.fa_operator.name || team.fa_operator.employee_name}`}
                sx={{ bgcolor: roleColors.fa_operator, color: 'white' }}
              />
            )}
            {team.test_operator && (
              <Chip
                size="small"
                label={`Test: ${team.test_operator.name || team.test_operator.employee_name}`}
                sx={{ bgcolor: roleColors.test_operator, color: 'white' }}
              />
            )}
            {team.junior_devs?.map((dev, idx) => (
              <Chip
                key={idx}
                size="small"
                label={`Junior: ${dev.name || dev.employee_name}`}
                sx={{ bgcolor: roleColors.junior_dev, color: 'white' }}
              />
            ))}
          </Box>

          {/* Tracking Summary */}
          {team.tracking_summary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>
                Tracking Data
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1, textAlign: 'center', bgcolor: colors.paper }}>
                    <Typography variant="h6" sx={{ color: colors.info }}>
                      {team.tracking_summary.total_prompts || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>Prompts</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1, textAlign: 'center', bgcolor: colors.paper }}>
                    <Typography variant="h6" sx={{ color: colors.warning }}>
                      {team.tracking_summary.total_tools || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>Tools</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1, textAlign: 'center', bgcolor: colors.paper }}>
                    <Typography variant="h6" sx={{ color: colors.success }}>
                      {team.tracking_summary.session_count || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>Sessions</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1, textAlign: 'center', bgcolor: colors.paper }}>
                    <Typography variant="h6" sx={{ color: colors.primary }}>
                      {team.tracking_summary.auto_approved_count || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>Auto</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Score Progress */}
          {team.total_score > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                Total Score: {team.total_score.toFixed(2)} / 100
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(team.total_score, 100)}
                sx={{ height: 8, borderRadius: 4, bgcolor: colors.grey200 }}
              />
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={(e) => { e.stopPropagation(); onSyncTracking(team); }}
              sx={{ borderColor: colors.primary, color: colors.primary }}
            >
              Sync Tracking
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<TrophyIcon />}
              onClick={(e) => { e.stopPropagation(); onScoreTeam(team); }}
              sx={{ bgcolor: colors.primary }}
            >
              Score Team
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

const HackathonManagementTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Data
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [kpiConfigs, setKpiConfigs] = useState([]);

  // Dialogs
  const [kpiDialog, setKpiDialog] = useState(false);
  const [scoreDialog, setScoreDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamScores, setTeamScores] = useState({});
  const [moduleDialog, setModuleDialog] = useState(false);
  const [newModule, setNewModule] = useState({ name: '', description: '', complexity_level: 'medium' });
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Load events list
  useEffect(() => {
    loadEvents();
  }, []);

  // Load event data when selected event changes
  useEffect(() => {
    if (selectedEventId) {
      loadEventData(selectedEventId);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await hackathonService.getEvents();
      setEvents(res.events || []);
      // Auto-select most recent active event
      const activeEvent = res.events?.find(e =>
        ['in_progress', 'team_formation', 'judging'].includes(e.status)
      ) || res.events?.[0];
      if (activeEvent) {
        setSelectedEventId(activeEvent.id);
      }
    } catch (err) {
      setError('Failed to load hackathon events');
    } finally {
      setLoading(false);
    }
  };

  const loadEventData = async (eventId) => {
    try {
      setLoading(true);
      const [eventRes, statsRes] = await Promise.all([
        hackathonService.getEvent(eventId),
        hackathonService.getEventStats(eventId)
      ]);
      setEvent(eventRes.event);
      setStats(statsRes);
      setKpiConfigs(eventRes.event.kpi_configs || []);

      // Load registrations
      const regRes = await hackathonService.getRegistrations(eventId);
      setRegistrations(regRes.registrations || []);

      // Load teams
      const teamsRes = await hackathonService.getTeams(eventId);
      setTeams(teamsRes.teams || []);

      // Load volunteers
      const volRes = await hackathonService.getVolunteers(eventId);
      setVolunteers(volRes.volunteers || []);

      // Load leaderboard
      if (['in_progress', 'judging', 'completed'].includes(eventRes.event.status)) {
        try {
          const lbRes = await hackathonService.getLeaderboard(eventId);
          setLeaderboard(lbRes);
        } catch (err) {
          console.log('Leaderboard not available');
        }
      }
    } catch (err) {
      setError('Failed to load hackathon data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTeams = async () => {
    try {
      setLoading(true);
      await hackathonService.generateTeams(selectedEventId);
      loadEventData(selectedEventId);
    } catch (err) {
      setError(err.message || 'Failed to generate teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAllTracking = async () => {
    try {
      setLoading(true);
      await hackathonService.syncAllTeamsTracking(selectedEventId);
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to sync tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTeamTracking = async (team) => {
    try {
      await hackathonService.syncTeamTracking(team.id);
      loadEventData(selectedEventId);
    } catch (err) {
      setError(`Failed to sync tracking for ${team.team_name}`);
    }
  };

  const handleUpdateKPIs = async () => {
    try {
      await hackathonService.updateKPIs(selectedEventId, kpiConfigs);
      setKpiDialog(false);
      loadEventData(selectedEventId);
    } catch (err) {
      setError(err.message || 'Failed to update KPIs');
    }
  };

  const handleSubmitScore = async () => {
    try {
      await hackathonService.submitTeamScore(selectedTeam.id, {
        kpi_scores: teamScores,
        comments: ''
      });
      setScoreDialog(false);
      setSelectedTeam(null);
      setTeamScores({});
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to submit score');
    }
  };

  const handleAddModule = async () => {
    try {
      await hackathonService.addModule(selectedEventId, newModule);
      setModuleDialog(false);
      setNewModule({ name: '', description: '', complexity_level: 'medium' });
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to add module');
    }
  };

  const handleFinalizeAwards = async () => {
    try {
      await hackathonService.finalizeAwards(selectedEventId);
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to finalize awards');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await hackathonService.updateEventStatus(selectedEventId, newStatus);
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      await hackathonService.importParticipants(selectedEventId, importFile, true);
      setImportDialog(false);
      setImportFile(null);
      loadEventData(selectedEventId);
    } catch (err) {
      setError('Failed to import participants');
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'draft': colors.grey500,
      'registration_open': colors.info,
      'registration_closed': colors.warning,
      'team_formation': colors.primary,
      'in_progress': colors.success,
      'judging': colors.warning,
      'completed': colors.success
    };
    return statusColors[status] || colors.grey500;
  };

  if (loading && !event) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Hackathon Management
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Manage teams, scoring, KPIs, and tracking for hackathon events
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Event</InputLabel>
            <Select
              value={selectedEventId || ''}
              onChange={(e) => setSelectedEventId(e.target.value)}
              label="Select Event"
            >
              {events.map((evt) => (
                <MenuItem key={evt.id} value={evt.id}>
                  {evt.name} ({evt.status?.replace('_', ' ')})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => selectedEventId && loadEventData(selectedEventId)}
            disabled={loading}
            sx={{ borderColor: colors.primary, color: colors.primary }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!selectedEventId ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Typography sx={{ color: colors.textSecondary }}>
            Select a hackathon event to manage
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Event Header */}
          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                    {event?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={event?.status?.replace('_', ' ')}
                      size="small"
                      sx={{ bgcolor: getStatusColor(event?.status), color: 'white', fontWeight: 600 }}
                    />
                    <Chip
                      label={`${new Date(event?.start_date).toLocaleDateString()} - ${new Date(event?.end_date).toLocaleDateString()}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => setKpiDialog(true)}
                    sx={{ borderColor: colors.primary, color: colors.primary }}
                  >
                    KPI Settings
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={event?.status || ''}
                      label="Status"
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="registration_open">Registration Open</MenuItem>
                      <MenuItem value="registration_closed">Registration Closed</MenuItem>
                      <MenuItem value="team_formation">Team Formation</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="judging">Judging</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Registrations"
                value={stats?.registrations?.total || 0}
                subtitle={`${stats?.registrations?.confirmed || 0} confirmed`}
                color={colors.info}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Teams"
                value={stats?.teams?.total_teams || 0}
                subtitle={`${stats?.teams?.modules_assigned || 0} modules assigned`}
                color={colors.primary}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Volunteers"
                value={stats?.volunteers?.total || 0}
                subtitle={`${stats?.volunteers?.jury || 0} jury`}
                color={colors.warning}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Modules"
                value={stats?.modules_count || event?.modules?.length || 0}
                subtitle="Available"
                color={colors.success}
              />
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                borderBottom: `1px solid ${colors.grey200}`,
                '& .MuiTab-root': { color: colors.textSecondary, textTransform: 'none', fontWeight: 600 },
                '& .Mui-selected': { color: colors.primary },
                '& .MuiTabs-indicator': { bgcolor: colors.primary }
              }}
            >
              <Tab icon={<GroupIcon sx={{ fontSize: 18 }} />} label="Teams" iconPosition="start" />
              <Tab icon={<TrophyIcon sx={{ fontSize: 18 }} />} label="Scoring" iconPosition="start" />
              <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Registrations" iconPosition="start" />
              <Tab icon={<AssessmentIcon sx={{ fontSize: 18 }} />} label="Tracking" iconPosition="start" />
            </Tabs>

            {/* Teams Tab */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    Teams ({teams.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setModuleDialog(true)}
                      sx={{ borderColor: colors.primary, color: colors.primary }}
                    >
                      Add Module
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<GroupIcon />}
                      onClick={handleGenerateTeams}
                      disabled={registrations.length < 5}
                      sx={{ bgcolor: colors.primary }}
                    >
                      Generate Teams
                    </Button>
                  </Box>
                </Box>

                {/* Modules */}
                {event?.modules?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>
                      Available Modules
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {event.modules.map((m) => (
                        <Chip
                          key={m.module_id}
                          label={m.name}
                          size="small"
                          sx={{
                            bgcolor: m.assigned_team_id ? colors.success : colors.grey200,
                            color: m.assigned_team_id ? 'white' : colors.textPrimary
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Teams List */}
                {teams.map((team) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    kpiConfigs={kpiConfigs}
                    onScoreTeam={(t) => { setSelectedTeam(t); setScoreDialog(true); }}
                    onSyncTracking={handleSyncTeamTracking}
                  />
                ))}

                {teams.length === 0 && (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: colors.grey50, borderRadius: 2 }}>
                    <GroupIcon sx={{ fontSize: 48, color: colors.grey500, mb: 2 }} />
                    <Typography sx={{ color: colors.textSecondary }}>
                      No teams generated yet. Import participants and click "Generate Teams".
                    </Typography>
                  </Paper>
                )}
              </Box>
            </TabPanel>

            {/* Scoring Tab */}
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    Scoring & Leaderboard
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SyncIcon />}
                      onClick={handleSyncAllTracking}
                      sx={{ borderColor: colors.primary, color: colors.primary }}
                    >
                      Sync All Tracking
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      startIcon={<TrophyIcon />}
                      onClick={handleFinalizeAwards}
                      disabled={event?.status !== 'judging'}
                    >
                      Finalize Awards
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: colors.grey50, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Rank</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Team</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Module</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Score</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(leaderboard?.teams || teams).map((team, idx) => (
                        <TableRow key={team.team_id || team.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {team.rank || idx + 1}
                              {team.rank === 1 && ' 🥇'}
                              {team.rank === 2 && ' 🥈'}
                              {team.rank === 3 && ' 🥉'}
                            </Typography>
                          </TableCell>
                          <TableCell>{team.team_name}</TableCell>
                          <TableCell>{team.module || team.assigned_module?.name || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {(team.total_score || 0).toFixed(2)}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(team.total_score || 0, 100)}
                                sx={{ width: 60, height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedTeam(team);
                                setScoreDialog(true);
                              }}
                              sx={{ borderColor: colors.primary, color: colors.primary }}
                            >
                              Score
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            {/* Registrations Tab */}
            <TabPanel value={activeTab} index={2}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    Participants ({registrations.length})
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => setImportDialog(true)}
                    sx={{ bgcolor: colors.primary }}
                  >
                    Import Excel
                  </Button>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400, bgcolor: colors.grey50, borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: colors.paper, fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ bgcolor: colors.paper, fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ bgcolor: colors.paper, fontWeight: 600 }}>Role</TableCell>
                        <TableCell sx={{ bgcolor: colors.paper, fontWeight: 600 }}>Rating</TableCell>
                        <TableCell sx={{ bgcolor: colors.paper, fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {registrations.map((reg) => (
                        <TableRow key={reg.id} hover>
                          <TableCell>{reg.employee_name}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{reg.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={reg.operator_role?.replace('_', ' ') || 'N/A'}
                              sx={{
                                bgcolor: roleColors[reg.operator_role] || colors.grey200,
                                color: roleColors[reg.operator_role] ? 'white' : colors.textPrimary,
                                fontSize: '0.7rem'
                              }}
                            />
                          </TableCell>
                          <TableCell>{reg.ailyce_rating || 0}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={reg.registration_status}
                              sx={{
                                bgcolor: reg.registration_status === 'confirmed' ? colors.success : colors.grey200,
                                color: reg.registration_status === 'confirmed' ? 'white' : colors.textPrimary,
                                fontSize: '0.7rem'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            {/* Tracking Tab */}
            <TabPanel value={activeTab} index={3}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    Claude Code Tracking Overview
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SyncIcon />}
                    onClick={handleSyncAllTracking}
                    sx={{ borderColor: colors.primary, color: colors.primary }}
                  >
                    Sync All Teams
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Tracking data is automatically synced from the Claude Code hooks system.
                  This shows prompts, tool calls, and sessions per team.
                </Alert>

                <Grid container spacing={2}>
                  {teams.map((team) => (
                    <Grid item xs={12} md={6} key={team.id}>
                      <Card elevation={1} sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary, mb: 1 }}>
                            {team.team_name}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          {team.tracking_summary ? (
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PlayIcon sx={{ color: colors.info, fontSize: 18 }} />
                                  <Typography variant="body2">
                                    Prompts: <strong>{team.tracking_summary.total_prompts || 0}</strong>
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CodeIcon sx={{ color: colors.warning, fontSize: 18 }} />
                                  <Typography variant="body2">
                                    Tools: <strong>{team.tracking_summary.total_tools || 0}</strong>
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AssessmentIcon sx={{ color: colors.primary, fontSize: 18 }} />
                                  <Typography variant="body2">
                                    Sessions: <strong>{team.tracking_summary.session_count || 0}</strong>
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CheckIcon sx={{ color: colors.success, fontSize: 18 }} />
                                  <Typography variant="body2">
                                    Auto: <strong>{team.tracking_summary.auto_approved_count || 0}</strong>
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
                              No tracking data yet
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>
          </Paper>
        </>
      )}

      {/* KPI Dialog */}
      <Dialog open={kpiDialog} onClose={() => setKpiDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>KPI Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Total weight must equal 100%. Current: {kpiConfigs.reduce((sum, k) => sum + k.weight_percentage, 0)}%
          </Alert>
          {kpiConfigs.map((kpi, idx) => (
            <Box key={kpi.kpi_id} sx={{ mb: 2, p: 2, border: `1px solid ${colors.grey200}`, borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{kpi.name}</Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>{kpi.description}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Weight: {kpi.weight_percentage}%</Typography>
              <Slider
                value={kpi.weight_percentage}
                onChange={(e, v) => {
                  const newConfigs = [...kpiConfigs];
                  newConfigs[idx].weight_percentage = v;
                  setKpiConfigs(newConfigs);
                }}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
              />
              {kpi.is_auto_calculated && (
                <Chip label="Auto-calculated from tracking" size="small" color="info" sx={{ mt: 1 }} />
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateKPIs}
            disabled={Math.abs(kpiConfigs.reduce((sum, k) => sum + k.weight_percentage, 0) - 100) > 0.01}
          >
            Save KPIs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Score Dialog */}
      <Dialog open={scoreDialog} onClose={() => setScoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Score Team: {selectedTeam?.team_name}</DialogTitle>
        <DialogContent>
          {kpiConfigs.filter(k => !k.is_auto_calculated).map((kpi) => (
            <Box key={kpi.kpi_id} sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {kpi.name} (Weight: {kpi.weight_percentage}%)
              </Typography>
              <Slider
                value={teamScores[kpi.kpi_id] || 0}
                onChange={(e, v) => setTeamScores({ ...teamScores, [kpi.kpi_id]: v })}
                min={0}
                max={kpi.max_score}
                step={0.5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitScore}>Submit Score</Button>
        </DialogActions>
      </Dialog>

      {/* Module Dialog */}
      <Dialog open={moduleDialog} onClose={() => setModuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Module</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Module Name"
            value={newModule.name}
            onChange={(e) => setNewModule({ ...newModule, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={newModule.description}
            onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Complexity</InputLabel>
            <Select
              value={newModule.complexity_level}
              label="Complexity"
              onChange={(e) => setNewModule({ ...newModule, complexity_level: e.target.value })}
            >
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddModule}>Add Module</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Participants from Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HackathonManagementTab;
